<template>
	<section class="section-base form-section-base">

		<div class="form-base">
			<Person
				v-if="role === Role.PERSON"
				:v="$v.person"
				:is-alternative-service-edit="isAlternativeServiceEdit"
				:is-surcharge="isSurcharge"
				:is-businessman="person.isBusinessman"
				translationKey="noticeRecovery.applicant.person"
			>
				<template>
					<div class="form-section-base__title">
						Сведения о взыскателе (заявителе)
					</div>
					<v-checkbox
						class="mb-1 mt-2"
						v-model="$v.person.isBusinessman.$model"
						label="Взыскатель является индивидуальным предпринимателем"
						hide-details
						:disabled="isFieldDisabled || isBusinessmanDisabled"
					/>
					<RoleSelector :is-foreign-organization-disabled="isForeignOrganizationDisabled"
												:is-organization-disabled="isOrganizationDisabled" :is-surcharge="isSurcharge" v-model="role"
												:role-type="roleType"/>
				</template>
			</Person>

			<ManualOrganization
				v-if="role === Role.ORGANIZATION"
				:v="$v.organization"
				:is-alternative-service-edit="isAlternativeServiceEdit"
				:is-surcharge="isSurcharge"
				:statement-type="statementType"
			>
				<template>
					<div class="form-section-base__title">
						Сведения о взыскателе (заявителе)
					</div>
					<RoleSelector :is-foreign-organization-disabled="isForeignOrganizationDisabled"
												:is-organization-disabled="isOrganizationDisabled" :is-surcharge="isSurcharge" v-model="role"
												:role-type="roleType"/>
				</template>
			</ManualOrganization>

			<ManualForeignOrganization
				v-if="role === Role.FOREIGN_ORGANIZATION"
				:v="$v.foreignOrganization"
				:is-alternative-service-edit="isAlternativeServiceEdit"
				:is-surcharge="isSurcharge"
				:statement-type="statementType"
			>
				<template>
					<div class="form-section-base__title">
						Сведения о взыскателе (заявителе)
					</div>
					<RoleSelector :is-foreign-organization-disabled="isForeignOrganizationDisabled"
												:is-organization-disabled="isOrganizationDisabled" :is-surcharge="isSurcharge" v-model="role"
												:role-type="roleType"/>
				</template>
			</ManualForeignOrganization>
		</div>
		<v-container>
			<Footer
				@next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
				@prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
				:first-step="isFirstStep"
				:loading-next="loadingNext"
			/>
		</v-container>
	</section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import {Role} from '@/utils/dictionaries';
import {checkInvalid, leaveStep} from '@/utils/services';
import {ServicesModule} from '@/store/modules/services';
import {AuthModule} from '@/store/modules/auth';
import Footer from '@/components/services/elements/Footer';
import {StepDirection, StepName} from '@/types/services';
import {trusteeModel} from '@/components/services/elements/TrusteeView';
import Person, {personModel, personValidation} from '../Person';
import ManualOrganization, {getManualOrganizationValidation, manualOrganizationModel,} from './ManualOrganization';
import ManualForeignOrganization, {
	getManualForeignOrganizationValidation,
	manualForeignOrganizationModel,
} from './ManualForeignOrganization';
import {NoticeRecoveryStep} from '@/constants';
import RoleSelector from "@/components/services/NoticeRecovery/RoleSelector.vue";

export default {
	name: 'Manual',
	props: {
		loadingNext: {
			type: Boolean,
			required: true,
		},
		roleType: {
			type: String,
			required: true,
		},
		isAlternativeServiceEdit: {
			type: Boolean,
			required: true,
		},
		isSurcharge: {
			type: Boolean,
			required: true,
		},
		statementType: {
			type: String,
			required: true,
		},
	},
	components: {
		RoleSelector,
		Person,
		ManualOrganization,
		ManualForeignOrganization,
		Footer,
	},
	data() {
		return {
			StepDirection,
			leaveStep,
			StepName,
			Role,
			role: this.roleType,
			invalidFields: [],
			person: cloneDeep(personModel),
			organization: cloneDeep(manualOrganizationModel),
			foreignOrganization: cloneDeep(manualForeignOrganizationModel),
			isBusinessmanDisabled: false
		};
	},
	computed: {
		isFieldDisabled() {
			if (!this.isManual) return false;

			return this.isManual && this.isAlternativeServiceEdit;
		},
		isOrganizationDisabled() {
			const isOrganization = this.roleType === Role.ORGANIZATION;
			const isApplicantOrganization = Boolean(
				ServicesModule.statement.subjects.applicant.organization
			);

			if (this.isSurcharge) return this.isSurcharge;

			return this.isAlternativeServiceEdit
				? !isApplicantOrganization
				: !isOrganization;
		},

		isForeignOrganizationDisabled() {
			const isForeignOrganization = [
				Role.FOREIGN_ORGANIZATION,
				Role.ORGANIZATION,
			].includes(this.roleType);
			const isApplicantForeignOrganization = Boolean(
				ServicesModule.statement.subjects.applicant.foreignOrganization
			);

			if (this.isSurcharge) return this.isSurcharge;

			return this.isAlternativeServiceEdit
				? !isApplicantForeignOrganization
				: !isForeignOrganization;
		},

		isFirstStep() {
			return NoticeRecoveryStep.APPLICANT === StepName.S1;
		},
	},
	validations() {
		return {
			person: personValidation,
			organization: getManualOrganizationValidation(
				this.organization,
				this.isAlternativeServiceEdit
			),
			foreignOrganization: getManualForeignOrganizationValidation(
				this.foreignOrganization,
				this.isAlternativeServiceEdit
			),
		};
	},
	created() {
		this.prefillDraft();
	},
	mounted() {
		const user = cloneDeep(AuthModule.user);
		const orgDetails = cloneDeep(AuthModule.orgDetails)
		const org = user?.roleDetails?.find(item => item.oid === orgDetails?.oid)
		this.person.isBusinessman = Boolean(org?.chief)
		this.isBusinessmanDisabled = this.person.isBusinessman
	},
	methods: {
		touchFields() {
			this.invalidFields = [];

			this.$v[this.role].$touch();
			checkInvalid(this.$v[this.role], this.invalidFields);
		},

		prefillDraft() {
			const applicantStore = cloneDeep(
				ServicesModule.statement.subjects.applicant
			);

			if (
				!applicantStore.person &&
				!applicantStore.organization &&
				!applicantStore.foreignOrganization
			) {
				this.prefillEsia();
				return;
			}

			if (
				applicantStore.trustee?.documents &&
				!applicantStore.trustee.documents.statusDocument
			) {
				applicantStore.trustee.documents.statusDocument = cloneDeep(
					trusteeModel.documents.statusDocument
				);
			}

			if (applicantStore.person) {
				this.role = Role.PERSON;
				this.person = deepmerge(personModel, applicantStore.person);
			}

			if (applicantStore.organization) {
				this.role = Role.ORGANIZATION;
				this.organization = deepmerge.all([
					manualOrganizationModel,
					applicantStore.organization,
					{
						trustee: applicantStore.trustee,
					},
				]);
			}

			if (applicantStore.foreignOrganization) {
				this.role = Role.FOREIGN_ORGANIZATION;
				this.foreignOrganization = deepmerge.all([
					manualForeignOrganizationModel,
					applicantStore.foreignOrganization,
					{
						trustee: applicantStore.trustee,
					},
				]);
			}
		},

		prefillEsia() {
			if (this.roleType === Role.PERSON) {
				this.prefillEsiaPerson();
			} else if (this.roleType === Role.ORGANIZATION) {
				this.prefillEsiaOrganization();
				this.prefillEsiaForeignOrganization();
			} else if (this.roleType === Role.FOREIGN_ORGANIZATION) {
				this.prefillEsiaForeignOrganization();
			}
		},

		async prefillEsiaPerson() {
			const user = cloneDeep(AuthModule.user);
			const orgDetails = cloneDeep(AuthModule.orgDetails);

			const addressPLV = await AuthModule.findByIdAddress(
				user.currentAddressInfo
			);
			const addressPRG = await AuthModule.findByIdAddress(
				user.registerAddressInfo
			);

			this.person = deepmerge(this.person, {
				lastName: user.lastName || null,
				firstName: user.firstName || null,
				patronymic: user.middleName || null,
				birthDate: user.birthDate || null,
				snils: user.snils || null,
				inn: user.inn || null,
				email: (user.contactInfo && user.contactInfo.email) || null,
				phone: (user.contactInfo && user.contactInfo.mobilePhone) || null,
				address: addressPLV ? addressPLV : addressPRG ? addressPRG : null,
				isBusinessman: this.person.isBusinessman || AuthModule.isBusinessman,
				ogrn: (orgDetails && orgDetails.ogrn) || (user.orgDetails && user.orgDetails.ogrn) || null,
				emailUl: (user.contactInfo && user.contactInfo.email) || null,
				regPlace: addressPLV ? addressPLV.addressStr : addressPRG ? addressPRG.addressStr : null,
			});
		},

		async prefillEsiaOrganization() {
			const userDetails = cloneDeep(AuthModule.user);
			const orgDetails = cloneDeep(AuthModule.orgDetails);

			const addressOLG = await AuthModule.findByIdAddress(
				orgDetails.addrDetails.registerAddressInfo
			);
			const addressOPS = await AuthModule.findByIdAddress(
				orgDetails.addrDetails.currentAddressInfo
			);

			this.organization = deepmerge(this.organization, {
				name: orgDetails.fullName || null,
				ogrn: orgDetails.ogrn || null,
				inn: orgDetails.inn || null,
				kpp: orgDetails.kpp || null,
				email: (orgDetails.cttsDetails && orgDetails.cttsDetails.email) || null,
				phone:
					(orgDetails.cttsDetails && orgDetails.cttsDetails.mobilePhone) ||
					null,
				address: addressOLG,
				factAddress: addressOPS,

				trustee: deepmerge(this.organization.trustee, {
					lastName: userDetails.lastName || null,
					firstName: userDetails.firstName || null,
					patronymic: userDetails.middleName || null,
					snils: userDetails.snils || null,
					inn: userDetails.inn || null,
					email:
						(userDetails.contactInfo && userDetails.contactInfo.email) || null,
					phone:
						(userDetails.contactInfo && userDetails.contactInfo.mobilePhone) ||
						null,
				}),
			});
		},

		async prefillEsiaForeignOrganization() {
			const userDetails = cloneDeep(AuthModule.user);
			const orgDetails = cloneDeep(AuthModule.orgDetails);

			const addressOLG = await AuthModule.findByIdAddress(
				orgDetails.addrDetails.registerAddressInfo
			);
			const addressOPS = await AuthModule.findByIdAddress(
				orgDetails.addrDetails.currentAddressInfo
			);

			this.foreignOrganization = deepmerge(this.foreignOrganization, {
				name: orgDetails.fullName || null,
				inn: orgDetails.inn || null,
				phone:
					(orgDetails.cttsDetails && orgDetails.cttsDetails.mobilePhone) ||
					null,
				address: addressOLG ? addressOLG : addressOPS ? addressOPS : null,

				trustee: deepmerge(this.foreignOrganization.trustee, {
					lastName: userDetails.lastName || null,
					firstName: userDetails.firstName || null,
					patronymic: userDetails.middleName || null,
					snils: userDetails.snils || null,
					inn: userDetails.inn || null,
					email:
						(userDetails.contactInfo && userDetails.contactInfo.email) || null,
					phone:
						(userDetails.contactInfo && userDetails.contactInfo.mobilePhone) ||
						null,
				}),
			});
		},

		assemblyModel() {
			const applicant = {};

			if (this.role === Role.PERSON && !this.person.isBusinessman) {
				this.person.ogrn = '';
				this.person.regDate = '';
				this.person.regPlace = '';
				this.person.emailUl = '';
			}

			applicant[this.role] = cloneDeep(this[this.role]);
			delete applicant[this.role].trustee;

			applicant.trustee = cloneDeep(this[this.role]).trustee;

			if (this.role === Role.PERSON && !this.person.isTrustee) {
				delete applicant.trustee;
			}

			if (
				((this.role === Role.PERSON && this.person.isTrustee) ||
					this.role !== Role.PERSON) &&
				!this[this.role].trustee.documents.statusDocument.name
			) {
				applicant.trustee.documents.statusDocument = null;
			}

			return applicant;
		},

		btnSaveClickHandler(direction) {
			leaveStep({
				stepThis: this,
				direction,
				step: NoticeRecoveryStep.APPLICANT,
			});
		},
	},
};
</script>
